import { useState, useEffect } from 'react';
import {
  BookOpen,
  Share2,
  Trash2,
  ChevronLeft,
  ChevronRight,
  MoreHorizontal,
  Feather,
  Map,
  Layers,
  Settings,
  StickyNote,
  BarChart3,
  Coffee,
  ShieldCheck,
  Trophy,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAppStore, type ViewMode, coverColors } from '@/lib/store';
import { useAuth } from '@/contexts/AuthContext';

interface SidebarProps {
  className?: string;
}

const navItems: { mode: ViewMode; label: string; icon: typeof BookOpen }[] = [
  { mode: 'manuscript', label: 'Manuscript', icon: Feather },
  { mode: 'stats', label: 'Stats', icon: BarChart3 },
  { mode: 'challenges', label: 'Challenges', icon: Trophy },
  { mode: 'dictionary', label: 'Dictionary', icon: BookOpen },
  { mode: 'constellation', label: 'Constellation', icon: Share2 },
  { mode: 'corkboard', label: 'Stickies', icon: StickyNote },
  { mode: 'timeline', label: 'Timeline', icon: Map },
  { mode: 'compost', label: 'Compost', icon: Layers },
];

export function Sidebar({ className }: SidebarProps) {
  const {
    viewMode,
    setViewMode,
    activeNovelId,
    novels,
    toggleBubblePanel,
    showBubblePanel,
    sidebarMode,
    setSidebarMode,
    themeProfile,
    setShowSettings,
    setChapterColor,
    setNovelCoverColor,
    createChapter,
    deleteChapter,
    setActiveChapter,
  } = useAppStore();

  const { user } = useAuth();
  const isAdmin = user?.email === 'lonnie@writelibrary.com';

  const [isCollapsed, setIsCollapsed] = useState(sidebarMode === 'pinned' ? false : true);
  const [showChapterMenu, setShowChapterMenu] = useState<string | null>(null);
  const [showColorPicker, setShowColorPicker] = useState<'novel' | string | null>(null);

  // Sync collapsed state with store
  useEffect(() => {
    setSidebarMode(isCollapsed ? 'hover' : 'pinned');
  }, [isCollapsed, setSidebarMode]);

  const activeNovel = novels.find(n => n.id === activeNovelId);
  const chapters = activeNovel?.chapters || [];

  const handleChapterClick = (chapterId: string) => {
    setActiveChapter(chapterId);
    setViewMode('manuscript');
  };

  const handleColorSelect = (color: string) => {
    if (showColorPicker === 'novel' && activeNovelId) {
      setNovelCoverColor(activeNovelId, color);
    } else if (showColorPicker && showColorPicker !== 'novel') {
      setChapterColor(showColorPicker, color);
    }
    setShowColorPicker(null);
  };

  return (
    <div
      className={cn(
        'fixed left-0 top-0 h-full flex flex-col transition-all duration-300 z-40',
        isCollapsed ? 'w-16' : 'w-56',
        className
      )}
      style={{
        backgroundColor: themeProfile.paperSurface,
        borderRight: `1px solid ${themeProfile.border}`
      }}
    >
      {/* Header */}
      <div
        className="flex items-center justify-between p-4"
        style={{ borderBottom: `1px solid ${themeProfile.border}` }}
      >
        {!isCollapsed && (
          <button
            onClick={() => setViewMode('dashboard')}
            className="flex items-center gap-2 group px-2 py-1.5 rounded-lg transition-colors"
            style={{
              backgroundColor: themeProfile.background,
              border: `1px solid ${themeProfile.border}`
            }}
          >
            <div
              className="w-7 h-7 rounded-md flex items-center justify-center transition-transform group-hover:scale-105 cursor-pointer"
              style={{ backgroundColor: activeNovel?.coverColor || themeProfile.accent }}
              onClick={(e) => {
                e.stopPropagation();
                setShowColorPicker('novel');
              }}
              title="Click to change novel color"
            >
              <BookOpen className="w-3.5 h-3.5" style={{ color: themeProfile.background }} />
            </div>
            <span
              className="font-semibold text-sm"
              style={{ color: themeProfile.text }}
            >
              View All
            </span>
          </button>
        )}

        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="p-1.5 rounded-lg transition-colors hover:opacity-70 min-w-10 min-h-10 flex items-center justify-center"
          style={{ color: themeProfile.text, opacity: 0.6 }}
          aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          aria-expanded={!isCollapsed}
        >
          {isCollapsed ? <ChevronRight className="w-4 h-4" aria-hidden="true" /> : <ChevronLeft className="w-4 h-4" aria-hidden="true" />}
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-4 px-2">
        {/* View Mode Buttons */}
        <div className="space-y-1 mb-6">
          {navItems.map(({ mode, label, icon: Icon }) => (
            <button
              key={mode}
              onClick={() => setViewMode(mode)}
              className={cn(
                'w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all',
                viewMode === mode && 'shadow-sm'
              )}
              style={{
                backgroundColor: viewMode === mode ? themeProfile.accent + '15' : 'transparent',
                color: viewMode === mode ? themeProfile.accent : themeProfile.text,
                opacity: viewMode === mode ? 1 : 0.7,
              }}
              title={isCollapsed ? label : undefined}
            >
              <Icon className="w-4 h-4 flex-shrink-0" />
              {!isCollapsed && <span>{label}</span>}
            </button>
          ))}

          {/* Admin Entry - Restricted */}
          {isAdmin && (
            <button
              onClick={() => setViewMode('admin')}
              className={cn(
                'w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-bold transition-all mt-4 border border-dashed',
                viewMode === 'admin' && 'shadow-sm'
              )}
              style={{
                backgroundColor: viewMode === 'admin' ? '#d97706' + '20' : 'transparent',
                borderColor: '#d9770640',
                color: viewMode === 'admin' ? '#d97706' : themeProfile.text,
              }}
              title={isCollapsed ? 'Developer Admin' : undefined}
            >
              <ShieldCheck className="w-4 h-4 flex-shrink-0" />
              {!isCollapsed && <span>Admin CRM</span>}
            </button>
          )}
        </div>

        {/* Chapters Section */}
        {!isCollapsed && activeNovel && (
          <div className="mt-6">
            <div className="flex items-center justify-between px-3 mb-2">
              <span
                className="text-xs font-medium uppercase tracking-wider"
                style={{ color: themeProfile.text, opacity: 0.7 }}
              >
                Chapters
              </span>
              <button
                onClick={() => createChapter()}
                className="w-6 h-6 rounded-md flex items-center justify-center transition-all hover:scale-110"
                style={{
                  backgroundColor: themeProfile.accent,
                  color: themeProfile.background
                }}
                title="Add chapter"
              >
                <span className="text-sm font-bold leading-none">+</span>
              </button>
            </div>

            <div className="space-y-0.5">
              {chapters.map((chapter) => (
                <div key={chapter.id} className="relative group">
                  <button
                    onClick={() => handleChapterClick(chapter.id)}
                    className={cn(
                      'w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all',
                      activeNovel.activeChapterId === chapter.id && 'bg-opacity-20'
                    )}
                    style={{
                      backgroundColor: activeNovel.activeChapterId === chapter.id
                        ? themeProfile.accent + '15'
                        : 'transparent',
                    }}
                  >
                    {/* Color Square */}
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowColorPicker(chapter.id);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.stopPropagation();
                          e.preventDefault();
                          setShowColorPicker(chapter.id);
                        }
                      }}
                      className="w-4 h-4 rounded flex-shrink-0 transition-transform hover:scale-110 cursor-pointer"
                      style={{
                        backgroundColor: chapter.color || themeProfile.accent,
                      }}
                      title="Click to change chapter color"
                    />
                    <span
                      className="truncate flex-1 text-left"
                      style={{
                        color: activeNovel.activeChapterId === chapter.id
                          ? themeProfile.text
                          : themeProfile.text,
                        opacity: activeNovel.activeChapterId === chapter.id ? 1 : 0.7,
                      }}
                    >
                      {chapter.title}
                    </span>

                    {/* Menu Button */}
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowChapterMenu(showChapterMenu === chapter.id ? null : chapter.id);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.stopPropagation();
                          e.preventDefault();
                          setShowChapterMenu(showChapterMenu === chapter.id ? null : chapter.id);
                        }
                      }}
                      className="opacity-0 group-hover:opacity-100 p-1 rounded transition-all cursor-pointer"
                      style={{ color: themeProfile.text, opacity: 0.5 }}
                    >
                      <MoreHorizontal className="w-3 h-3" />
                    </div>
                  </button>

                  {/* Chapter Menu */}
                  {showChapterMenu === chapter.id && (
                    <div
                      className="absolute right-0 top-full z-20 py-1 rounded-lg min-w-[120px] mt-1"
                      style={{
                        backgroundColor: themeProfile.paperSurface,
                        boxShadow: `0 4px 12px ${themeProfile.border}`,
                        border: `1px solid ${themeProfile.border}`
                      }}
                    >
                      <button
                        onClick={() => {
                          if (window.confirm(`Are you sure you want to delete "${chapter.title}"? It will be moved to the Compost Heap.`)) {
                            deleteChapter(chapter.id);
                            setShowChapterMenu(null);
                          }
                        }}
                        className="w-full px-3 py-2 text-left text-sm flex items-center gap-2 transition-colors hover:text-red-500"
                        style={{ color: themeProfile.text }}
                      >
                        <Trash2 className="w-3 h-3" />
                        Delete
                      </button>
                    </div>
                  )}
                </div>
              ))}

              {chapters.length === 0 && (
                <div
                  className="px-3 py-4 text-center text-sm"
                  style={{ color: themeProfile.text, opacity: 0.5 }}
                >
                  No chapters yet
                </div>
              )}
            </div>
          </div>
        )}
      </nav>

      {/* Color Picker Modal */}
      {showColorPicker && (
        <div
          className="absolute inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
          onClick={() => setShowColorPicker(null)}
        >
          <div
            className="p-4 rounded-xl"
            style={{
              backgroundColor: themeProfile.paperSurface,
              border: `1px solid ${themeProfile.border}`
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3
              className="text-sm font-medium mb-3"
              style={{ color: themeProfile.text }}
            >
              {showColorPicker === 'novel' ? 'Choose Novel Color' : 'Choose Chapter Color'}
            </h3>
            <div className="grid grid-cols-4 gap-2">
              {coverColors.map((color) => (
                <button
                  key={color}
                  onClick={() => handleColorSelect(color)}
                  className="w-8 h-8 rounded-lg transition-transform hover:scale-110"
                  style={{ backgroundColor: color }}
                />
              ))}
            </div>
            <button
              onClick={() => setShowColorPicker(null)}
              className="mt-4 w-full py-2 rounded-lg text-sm"
              style={{
                backgroundColor: themeProfile.border,
                color: themeProfile.text
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Footer */}
      <div
        className="p-3"
        style={{ borderTop: `1px solid ${themeProfile.border}` }}
      >
        {!isCollapsed && activeNovel && (
          <div className="space-y-2">
            {/* Word Count */}
            <div
              className="px-3 py-2 rounded-lg"
              style={{ backgroundColor: themeProfile.background }}
            >
              <div className="flex items-center justify-between text-xs mb-1">
                <span style={{ color: themeProfile.text, opacity: 0.5 }}>Words</span>
                <span style={{ color: themeProfile.text }}>
                  {activeNovel.chapters.reduce((sum, ch) => sum + (ch.wordCount || 0), 0).toLocaleString()}
                </span>
              </div>
              <div
                className="h-1.5 rounded-full overflow-hidden"
                style={{ backgroundColor: themeProfile.border }}
              >
                <div
                  className="h-full rounded-full transition-all duration-500"
                  style={{
                    backgroundColor: themeProfile.accent,
                    width: `${Math.min(
                      (activeNovel.chapters.reduce((sum, ch) => sum + (ch.wordCount || 0), 0) / activeNovel.targetWordCount) * 100,
                      100
                    )}%`
                  }}
                />
              </div>
            </div>

            {/* Bubble Panel Toggle */}
            <button
              onClick={toggleBubblePanel}
              className={cn(
                'w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-all'
              )}
              style={{
                backgroundColor: showBubblePanel ? themeProfile.accent + '15' : themeProfile.background,
                color: showBubblePanel ? themeProfile.accent : themeProfile.text,
              }}
            >
              <span>Bubbles/Notes</span>
              <div
                className="w-8 h-4 rounded-full relative transition-colors"
                style={{
                  backgroundColor: showBubblePanel ? themeProfile.accent : themeProfile.border
                }}
              >
                <div
                  className="absolute top-0.5 w-3 h-3 rounded-full transition-all"
                  style={{
                    backgroundColor: themeProfile.background,
                    left: showBubblePanel ? 'calc(100% - 14px)' : '2px'
                  }}
                />
              </div>
            </button>

            {/* Link to Public Discovery Page */}
            <a
              href="/read"
              className={cn(
                'w-full flex items-center justify-between px-3 py-2 mt-4 rounded-lg text-sm font-semibold transition-all border shadow-sm'
              )}
              style={{
                backgroundColor: themeProfile.paperSurface,
                borderColor: themeProfile.accent,
                color: themeProfile.accent,
              }}
            >
              <span className="flex items-center gap-2">
                <Coffee className="w-4 h-4" />
                Enter Coffee Shop
              </span>
            </a>
          </div>
        )}

        {/* Settings Button */}
        <button
          onClick={() => setShowSettings(true)}
          className={cn(
            'w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all mt-2',
            isCollapsed && 'justify-center'
          )}
          style={{
            backgroundColor: 'transparent',
            color: themeProfile.text,
            opacity: 0.7,
          }}
          title={isCollapsed ? 'Settings' : undefined}
        >
          <Settings className="w-4 h-4 flex-shrink-0" />
          {!isCollapsed && <span>Settings</span>}
        </button>
      </div>
    </div>
  );
}
